import React, { useState, useEffect } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaUser, FaEnvelope, FaPhone, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import useForceLightTheme from "../../hooks/useForceLightTheme";
import { useBranding } from "../../contexts/BrandingContext";
import vcLogo from "../../Assets/Images/Logos/logo.png";
import vcFavIcon from "../../Assets/Images/Logos/favicon.png";
import lmsLogo from "../../Assets/Images/Logos/lms_logo.png";
import lmsFavIcon from "../../Assets/Images/Logos/favicon.png";
import studentImg from "../../Assets/Images/login_bg.png";
import "../Auth/Login.scss";

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith("lmscampus.com") || h === "localhost";
};
const logo = isLmsCampus() ? lmsLogo : vcLogo;
const favIcon = isLmsCampus() ? lmsFavIcon : vcFavIcon;

const JoinInstitute = () => {
  useForceLightTheme();
  const branding = useBranding();
  const brandLogo = branding.logoUrl || logo;
  const brandFavIcon = branding.faviconUrl || branding.logoUrl || favIcon;
  const brandName = branding.brandName;
  const navigate = useNavigate();

  const query = new URLSearchParams(useLocation().search);
  const inviteCode = query.get("code") || "";

  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", confirmPassword: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [instituteName, setInstituteName] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Validate invite code on mount
  useEffect(() => {
    if (!inviteCode) {
      setValidating(false);
      setMsg({ type: "danger", text: "No invite code provided. Please use the invite link shared by your institute." });
      return;
    }
    const validate = async () => {
      try {
        const { data } = await apiClient.get(`/auth/invite/validate?code=${encodeURIComponent(inviteCode)}`);
        setInviteValid(true);
        setInstituteName(data.brandName || data.instituteName);
      } catch (err) {
        setMsg({ type: "danger", text: err?.response?.data?.message || "Invalid or expired invite link." });
      }
      setValidating(false);
    };
    validate();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setMsg({ type: "", text: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!acceptedTerms) {
      setMsg({ type: "danger", text: "Please accept the Terms & Conditions to continue." });
      return;
    }
    if (!form.name.trim()) {
      setMsg({ type: "danger", text: "Name is required." });
      return;
    }
    if (!form.mobile.trim()) {
      setMsg({ type: "danger", text: "Mobile number is required." });
      return;
    }
    if (form.password.length < 6) {
      setMsg({ type: "danger", text: "Password must be at least 6 characters." });
      return;
    }
    if (form.password !== form.confirmPassword) {
      setMsg({ type: "danger", text: "Passwords do not match." });
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/join-institute", {
        code: inviteCode,
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        mobile: form.mobile.trim(),
        password: form.password,
      });

      // Auto-login: store token and redirect to student dashboard
      if (data.token) {
        localStorage.setItem("token", data.token);
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        window.dispatchEvent(new Event('vc-auth-change'));
        setMsg({ type: "success", text: data.message || "Registration successful!" });
        setTimeout(() => navigate("/s/"), 1000);
      } else {
        setMsg({ type: "success", text: data.message || "Registration successful! Please login." });
      }
    } catch (err) {
      setMsg({ type: "danger", text: err?.response?.data?.message || "Registration failed. Please try again." });
    }
    setLoading(false);
  };

  // Validating state
  if (validating) {
    return (
      <div className="auth-page" style={{ justifyContent: "center", alignItems: "center" }}>
        <Spinner animation="border" variant="primary" />
        <p className="mt-3" style={{ color: "#64748b" }}>Validating invite link...</p>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* Mobile top navbar */}
      <div className="auth-mobile-navbar">
        <Link to="/" className="auth-mobile-navbar-logo">
          <img src={brandFavIcon} alt="Logo" />
          <span>{brandName}</span>
        </Link>
        <Link to="/login" className="auth-mobile-navbar-btn">
          Sign In
        </Link>
      </div>

      <div className="auth-container">
        {/* Left: Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-badge">
              <img src={brandFavIcon} alt="Logo" className="auth-brand-icon" />
            </div>
            <h1 className="auth-brand-title">{instituteName || brandName}</h1>
            <p className="auth-brand-subtitle">
              You have been invited to join as a student
            </p>
            <div className="auth-brand-illustration">
              <img src={studentImg} alt="Learning illustration" />
            </div>
            <div className="auth-brand-features">
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Access courses and materials</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Join live classes</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Track your progress</span>
              </div>
            </div>
          </div>
          <div className="auth-brand-footer">
            <img src={brandLogo} alt={brandName} className="auth-brand-logo" />
          </div>
        </div>

        {/* Right: Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-form-wrapper">
            <div className="auth-form-header">
              <h2>Join {instituteName || "Institute"}</h2>
              <p>Create your student account to get started</p>
            </div>

            {msg.text && <Alert variant={msg.type}>{msg.text}</Alert>}

            {!inviteValid ? (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <p style={{ color: "#64748b" }}>
                  Already have an account?{" "}
                  <Link to="/login" style={{ color: "#4069e5" }}>Sign in</Link>
                </p>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-input-group">
                  <label>Full Name <span style={{ color: "red" }}>*</span></label>
                  <div className="auth-input-wrapper">
                    <FaUser className="auth-input-icon" />
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div className="auth-input-group">
                  <label>Email Address</label>
                  <div className="auth-input-wrapper">
                    <FaEnvelope className="auth-input-icon" />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com (optional)"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="auth-input-group">
                  <label>Mobile Number <span style={{ color: "red" }}>*</span></label>
                  <div className="auth-input-wrapper">
                    <FaPhone className="auth-input-icon" />
                    <input
                      type="tel"
                      name="mobile"
                      value={form.mobile}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      required
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <div className="auth-input-group">
                  <label>Password <span style={{ color: "red" }}>*</span></label>
                  <div className="auth-input-wrapper">
                    <FaLock className="auth-input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="Min 6 characters"
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                    <button type="button" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="auth-input-group">
                  <label>Confirm Password <span style={{ color: "red" }}>*</span></label>
                  <div className="auth-input-wrapper">
                    <FaLock className="auth-input-icon" />
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                    <button type="button" className="auth-password-toggle" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                      {showConfirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="auth-terms">
                  <label className="auth-checkbox-label">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the{" "}
                      <Link to="/terms-and-conditions" target="_blank">Terms & Conditions</Link>
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating Account...
                    </>
                  ) : (
                    "Join Institute"
                  )}
                </Button>

                <div className="auth-form-footer">
                  <p>
                    Already have an account?{" "}
                    <Link to="/login">Sign in</Link>
                  </p>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinInstitute;
